/**
 * Space on a focused client row: the client at a glance (cases, team,
 * status, portal) from what the list already loaded, plus the two follow-ups
 * found elsewhere: an unsent portal invite and same-name duplicates to merge.
 */
import dayjs from "dayjs";
import { ArrowUpRight, Copy, Mail } from "lucide-react";
import type { FC } from "react";

import type { ForeignNational, UserProfile } from "@/api/types";
import type { MatterDTO } from "@/api/types/matter";
import { VisaTag } from "@/components/common/VisaTag";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  PeekAction,
  PeekActions,
  PeekPanel,
  PeekProp,
  PeekProps
} from "@/components/ui/peek-panel";
import { Skeleton } from "@/components/ui/skeleton";
import { AssignedUsersAvatarStack } from "@/features/foreignNationals/components/AssignedUsersAvatarStack";
import { CaseStatusPill } from "@/features/matters/components/CaseStatusPill";
import { asVisaValue } from "@/utils/matterDisplay";

import { initialsFromName } from "../clientsListFilters";
import {
  CitizenshipCell,
  deadlineCountdown,
  deadlineDate,
  deadlineLabel,
  deadlineToneClass,
  PortalState,
  statusChip
} from "./ClientListCells";

const CASES_SHOWN = 3;

const profileName = (p: UserProfile | null | undefined): string =>
  [p?.first_name, p?.last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ") || (p?.email ?? "").trim();

type Props = {
  client: ForeignNational;
  fullName: string;
  avatar?: string | null;
  /** The client's cases; undefined while they load. */
  matters: MatterDTO[] | undefined;
  getUserProfile: (uid: string) => UserProfile | null | undefined;
  onOpen: () => void;
  onOpenMatter: (matter: MatterDTO) => void;
  onOpenPortal: () => void;
  onReviewDuplicate: (otherId: string) => void;
};

export const ClientPeek: FC<Props> = ({
  client,
  fullName,
  avatar,
  matters,
  getUserProfile,
  onOpen,
  onOpenMatter,
  onOpenPortal,
  onReviewDuplicate
}) => {
  const meta = client.list_meta;
  const teamIds = (client.assigned_to_user_ids ?? []).filter(Boolean);
  const teamNames = teamIds
    .map((uid) => profileName(getUserProfile(uid)))
    .filter(Boolean);
  const status = meta?.status.status ? statusChip(meta.status) : null;
  const deadline = meta?.next_deadline ?? null;
  const deadlineText = deadline ? deadlineLabel(deadline, matters) : null;
  const dupes = meta?.possible_duplicates ?? [];
  const notInvited = meta?.portal.state === "off";
  const shown = (matters ?? []).slice(0, CASES_SHOWN);

  return (
    <PeekPanel aria-label={`${fullName} preview`} data-testid="client-peek">
      <div className="flex items-start gap-2.5">
        <Avatar size="lg">
          {avatar ? <AvatarImage src={avatar} alt="" /> : null}
          <AvatarFallback>{initialsFromName(fullName)}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-lg font-medium text-fg">
            {fullName}
          </span>
          <span className="truncate text-sm text-fg-3">
            {client.email || "No email"}
          </span>
        </div>
        <Button size="xs" onClick={onOpen}>
          Open
          <ArrowUpRight />
        </Button>
      </div>

      <PeekProps>
        <PeekProp label="Cases" align="start">
          {matters === undefined ? (
            <Skeleton className="h-5 w-24" />
          ) : shown.length === 0 ? (
            <span className="text-fg-4">No cases yet</span>
          ) : (
            <div className="flex flex-col gap-1">
              {shown.map((m) => (
                <button
                  key={`${m.case_id}-${m.individual_id}`}
                  type="button"
                  onClick={() => onOpenMatter(m)}
                  aria-label={`Open the ${String(m.case?.visa ?? "")} case`}
                  className="flex items-center gap-1.5 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <VisaTag visaType={asVisaValue(String(m.case?.visa ?? ""))} />
                  <CaseStatusPill status={m.case?.status ?? ""} />
                </button>
              ))}
              {(matters?.length ?? 0) > shown.length ? (
                <span className="text-sm text-fg-3">
                  {(matters?.length ?? 0) - shown.length} more
                </span>
              ) : null}
            </div>
          )}
        </PeekProp>
        <PeekProp label="Team">
          {teamIds.length ? (
            <>
              <AssignedUsersAvatarStack userIds={teamIds} nonInteractive />
              <span className="min-w-0 truncate text-fg-2">
                {teamNames.join(", ")}
              </span>
            </>
          ) : (
            <span className="text-fg-4">Unassigned</span>
          )}
        </PeekProp>
        <PeekProp label="Citizenship">
          <CitizenshipCell meta={meta} />
        </PeekProp>
        {meta?.status.status ? (
          <PeekProp label="Status">
            <span>{meta.status.status}</span>
            {status ? <span className="text-fg-3">· {status.text}</span> : null}
          </PeekProp>
        ) : null}
        {deadline ? (
          <PeekProp label="Next Deadline">
            <span className="tabular-nums">{deadlineDate(deadline)}</span>
            <span className={deadlineToneClass(deadline)}>
              · {deadlineCountdown(deadline).text}
            </span>
            {deadlineText ? (
              <span className="min-w-0 truncate text-fg-3">
                · {deadlineText}
              </span>
            ) : null}
          </PeekProp>
        ) : null}
        {meta && meta.portal.state !== "unknown" ? (
          <PeekProp label="Portal">
            <PortalState meta={meta} />
            {meta.portal.last_sign_in ? (
              <span className="text-sm text-fg-4">
                Seen {dayjs(meta.portal.last_sign_in).format("MMM D")}
              </span>
            ) : null}
          </PeekProp>
        ) : null}
        {client.created_at ? (
          <PeekProp label="Created">
            <span className="text-fg-2 tabular-nums">
              {dayjs(client.created_at).format("MMM D, YYYY")}
            </span>
          </PeekProp>
        ) : null}
      </PeekProps>

      {notInvited || dupes.length ? (
        <PeekActions>
          {notInvited ? (
            <PeekAction
              icon={Mail}
              title="Portal Invite Never Sent"
              description="Send one from Overview so they know how to sign in."
              action={
                <Button size="xs" onClick={onOpenPortal}>
                  Invite…
                </Button>
              }
              data-testid="client-peek-invite"
            />
          ) : null}
          {dupes.length ? (
            <PeekAction
              icon={Copy}
              tone="warn"
              title={`${dupes.length} Possible ${
                dupes.length === 1 ? "Duplicate" : "Duplicates"
              }`}
              description={`${dupes
                .map((d) => d.name)
                .join(
                  ", "
                )}. Merging moves their cases, requests, documents and history here.`}
              action={
                <Button
                  size="xs"
                  onClick={() =>
                    onReviewDuplicate(dupes[0].foreign_national_id)
                  }
                >
                  Merge…
                </Button>
              }
              data-testid="client-peek-duplicates"
            />
          ) : null}
        </PeekActions>
      ) : null}
    </PeekPanel>
  );
};
